import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import './HoldingForm.css';
import { API_BASE_URL } from './lib/api';

function HoldingForm({ holding = null, onSuccess, onCancel }) {
  const isEdit = holding !== null;
  const isInitialMount = useRef(true);
  const firstInputRef = useRef(null);
  const modalRef = useRef(null);

  const [formData, setFormData] = useState({
    assetName: holding?.assetName || '',
    assetType: holding?.assetType || 'Stock',
    subType: holding?.subType || '',
    ticker: holding?.ticker || '',
    currency: holding?.currency || 'USD',
    units: holding?.units || '',
    purchasePrice: holding?.purchasePrice || '',
    purchaseExchangeRate: holding?.purchaseExchangeRate || '',
    platform: holding?.platform || '',
    dateAcquired: holding?.dateAcquired || new Date().toISOString().split('T')[0],
    // New fields for manual assets and liabilities
    useManualValue: holding?.useManualValue || false,
    manualCurrentValue: holding?.manualCurrentValue || '',
    lastManualUpdate: holding?.lastManualUpdate || '',
    notes: holding?.notes || '',
    isLiability: holding?.isLiability || false,
    interestRate: holding?.interestRate || '',
    monthlyPayment: holding?.monthlyPayment || '',
    maturityDate: holding?.maturityDate || '',
    linkedAssetId: holding?.linkedAssetId || '',
    // Vehicle/Equipment-specific fields
    vinOrSerial: holding?.vinOrSerial || '',
    condition: holding?.condition || '',
    depreciationRate: holding?.depreciationRate || '',
    insuranceDetails: holding?.insuranceDetails || '',
    maintenanceLog: holding?.maintenanceLog || '',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fetchingRate, setFetchingRate] = useState(false);
  const [exchangeRateError, setExchangeRateError] = useState(null);
  const [realEstateHoldings, setRealEstateHoldings] = useState([]);

  // Define manual asset types
  const MANUAL_ASSET_TYPES = ['Cash/Bank Account', 'Real Estate & Property', 'Vehicle', 'Equipment', 'Other High-Value Asset'];
  const LIABILITY_TYPES = ['Mortgage', 'Loan', 'Credit Card Debt', 'Other Liability'];
  const ALL_MANUAL_TYPES = [...MANUAL_ASSET_TYPES, ...LIABILITY_TYPES];
  const VEHICLE_EQUIPMENT_TYPES = ['Vehicle', 'Equipment', 'Other High-Value Asset'];

  // Define real estate sub-types
  const REAL_ESTATE_SUBTYPES = [
    'House (Primary Residence)',
    'House (Rental Property)',
    'Apartment/Condo',
    'Land (Undeveloped)',
    'Land (Agricultural)',
    'Commercial Property',
    'Vacation Home'
  ];

  // Determine if current asset type is manual or liability
  const isManualAsset = ALL_MANUAL_TYPES.includes(formData.assetType);
  const isLiability = LIABILITY_TYPES.includes(formData.assetType);
  const isVehicleEquipment = VEHICLE_EQUIPMENT_TYPES.includes(formData.assetType);
  const isRealEstate = formData.assetType === 'Real Estate & Property' || formData.assetType === 'Real Estate';

  // Modal keyboard accessibility - focus management and keyboard trap
  useEffect(() => {
    // Focus first input when modal opens
    if (firstInputRef.current) {
      firstInputRef.current.focus();
    }

    // Keyboard event handler
    const handleKeyDown = (e) => {
      // Close modal on Escape key
      if (e.key === 'Escape') {
        onCancel();
        return;
      }

      // Trap focus within modal on Tab key
      if (e.key === 'Tab' && modalRef.current) {
        const focusableElements = modalRef.current.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (e.shiftKey && document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        } else if (!e.shiftKey && document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onCancel]);

  // Auto-fetch historical exchange rate when date or currency changes
  useEffect(() => {
    const fetchHistoricalRate = async () => {
      // Skip on initial mount when editing (preserve backend's exchange rate)
      if (isEdit && isInitialMount.current) {
        isInitialMount.current = false;
        return;
      }

      // For EUR (base currency), always set rate to 1.0
      if (formData.currency === 'EUR') {
        setFormData(prev => ({ ...prev, purchaseExchangeRate: '1.0' }));
        return;
      }

      // Don't fetch if no date
      if (!formData.dateAcquired) {
        return;
      }

      // Now the rate will be refetched whenever currency or date changes
      // (after initial mount for edit forms)

      setFetchingRate(true);
      setExchangeRateError(null);
      try {
        // Fetch currency → EUR rate (EUR is the base currency)
        const response = await axios.get(`https://api.frankfurter.app/${formData.dateAcquired}`, {
          params: {
            from: formData.currency,
            to: 'EUR'
          }
        });

        if (response.data && response.data.rates && response.data.rates.EUR) {
          const currencyToEUR = response.data.rates.EUR; // Direct currency → EUR rate
          setFormData(prev => ({ ...prev, purchaseExchangeRate: currencyToEUR.toFixed(4) }));
          setExchangeRateError(null);
        }
      } catch (err) {
        console.warn('Could not fetch historical rate:', err.message);
        // Fall back to fetching latest rate
        try {
          const fallbackResponse = await axios.get('https://api.frankfurter.app/latest', {
            params: {
              from: formData.currency,
              to: 'EUR'
            }
          });
          if (fallbackResponse.data && fallbackResponse.data.rates && fallbackResponse.data.rates.EUR) {
            const currencyToEUR = fallbackResponse.data.rates.EUR;
            setFormData(prev => ({ ...prev, purchaseExchangeRate: currencyToEUR.toFixed(4) }));
            setExchangeRateError('Using latest rate (historical rate unavailable)');
          }
        } catch (fallbackErr) {
          console.error('Fallback rate fetch also failed:', fallbackErr.message);
          setExchangeRateError('Unable to fetch exchange rate. Please enter manually.');
        }
      } finally {
        setFetchingRate(false);
      }
    };

    fetchHistoricalRate();
  }, [formData.dateAcquired, formData.currency, isEdit]);

  // Fetch real estate holdings when asset type is Mortgage
  useEffect(() => {
    const fetchRealEstateHoldings = async () => {
      if (formData.assetType !== 'Mortgage') {
        setRealEstateHoldings([]);
        return;
      }

      try {
        const response = await axios.get(`${API_BASE_URL}/holdings`, {
          withCredentials: true
        });

        // Filter for Real Estate holdings only
        const realEstate = response.data.filter(h => h.assetType === 'Real Estate' && !h.isLiability);
        setRealEstateHoldings(realEstate);
      } catch (err) {
        console.error('Failed to fetch real estate holdings:', err);
      }
    };

    fetchRealEstateHoldings();
  }, [formData.assetType]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Handle asset type change specially
    if (name === 'assetType') {
      const isManual = ALL_MANUAL_TYPES.includes(value);
      const isLiab = LIABILITY_TYPES.includes(value);

      setFormData(prev => ({
        ...prev,
        [name]: value,
        useManualValue: isManual,
        isLiability: isLiab,
        // Auto-set defaults for manual assets
        ...(isManual ? {
          ticker: '',
          units: '1',
          lastManualUpdate: new Date().toISOString()
        } : {})
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Prepare payload with appropriate fields based on manual/API asset type
      const payload = { ...formData };

      if (isManualAsset) {
        // For manual assets, ensure required fields are set
        payload.useManualValue = true;
        payload.lastManualUpdate = new Date().toISOString();
        // Set units to 1 for manual assets (backend expects this)
        if (!payload.units) payload.units = '1';
      }

      let response;
      if (isEdit) {
        // Update existing holding
        response = await axios.put(
          `${API_BASE_URL}/holdings/${holding.holdingId}`,
          payload,
          { withCredentials: true }
        );
      } else {
        // Create new holding
        response = await axios.post(
          `${API_BASE_URL}/holdings`,
          payload,
          { withCredentials: true }
        );
      }

      // Pass the created/updated holding back to parent
      onSuccess(response.data);
    } catch (err) {
      let errorMsg = err.response?.data?.details || err.response?.data?.error || err.message;

      // Handle case where error is an object (e.g., validation error with {field, message, type})
      if (typeof errorMsg === 'object' && errorMsg !== null) {
        errorMsg = errorMsg.message || JSON.stringify(errorMsg);
      }

      console.error('Failed to save holding:', err);
      console.error('Error response:', err.response?.data);
      setError(errorMsg);
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div
        className="modal-content"
        ref={modalRef}
        role="dialog"
        aria-labelledby="modal-title"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="modal-title">{isEdit ? 'Edit Holding' : 'Add New Holding'}</h2>

        {error && (
          <div className="error-message" role="alert">
            <strong>⚠️ Error:</strong> {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="holding-form">

          {/* STEP 1: Asset Type Selection - HERO ELEMENT */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{
              display: 'block',
              fontSize: '16px',
              fontWeight: '600',
              marginBottom: '12px',
              color: 'var(--color-text-primary)'
            }}>
              What type of asset are you adding? *
            </label>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
              gap: '12px',
              marginBottom: '16px'
            }}>
              {/* Investment Category */}
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, assetType: 'Stock', useManualValue: false, isLiability: false }))}
                style={{
                  padding: '16px 12px',
                  border: !isManualAsset && !isLiability ? '2px solid #667eea' : '2px solid var(--color-border)',
                  borderRadius: '8px',
                  background: !isManualAsset && !isLiability ? 'rgba(102, 126, 234, 0.1)' : 'var(--color-surface)',
                  cursor: 'pointer',
                  textAlign: 'center',
                  transition: 'all 0.2s',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: 'var(--color-text-primary)'
                }}
                aria-pressed={!isManualAsset && !isLiability}
              >
                <div style={{ fontSize: '24px', marginBottom: '4px' }}>📊</div>
                <div>Investment</div>
                <div style={{ fontSize: '11px', opacity: 0.7, marginTop: '4px' }}>Stocks, ETFs, Crypto</div>
              </button>

              {/* Cash Category */}
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, assetType: 'Cash/Bank Account', useManualValue: true, isLiability: false, ticker: '', units: '1' }))}
                style={{
                  padding: '16px 12px',
                  border: formData.assetType === 'Cash/Bank Account' ? '2px solid #667eea' : '2px solid var(--color-border)',
                  borderRadius: '8px',
                  background: formData.assetType === 'Cash/Bank Account' ? 'rgba(102, 126, 234, 0.1)' : 'var(--color-surface)',
                  cursor: 'pointer',
                  textAlign: 'center',
                  transition: 'all 0.2s',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: 'var(--color-text-primary)'
                }}
                aria-pressed={formData.assetType === 'Cash/Bank Account'}
              >
                <div style={{ fontSize: '24px', marginBottom: '4px' }}>💰</div>
                <div>Cash</div>
                <div style={{ fontSize: '11px', opacity: 0.7, marginTop: '4px' }}>Bank accounts</div>
              </button>

              {/* Real Estate Category */}
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, assetType: 'Real Estate & Property', useManualValue: true, isLiability: false, ticker: '', units: '1' }))}
                style={{
                  padding: '16px 12px',
                  border: formData.assetType === 'Real Estate & Property' || formData.assetType === 'Real Estate' ? '2px solid #667eea' : '2px solid var(--color-border)',
                  borderRadius: '8px',
                  background: formData.assetType === 'Real Estate & Property' || formData.assetType === 'Real Estate' ? 'rgba(102, 126, 234, 0.1)' : 'var(--color-surface)',
                  cursor: 'pointer',
                  textAlign: 'center',
                  transition: 'all 0.2s',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: 'var(--color-text-primary)'
                }}
                aria-pressed={formData.assetType === 'Real Estate & Property' || formData.assetType === 'Real Estate'}
              >
                <div style={{ fontSize: '24px', marginBottom: '4px' }}>🏠</div>
                <div>Real Estate</div>
                <div style={{ fontSize: '11px', opacity: 0.7, marginTop: '4px' }}>Property, land</div>
              </button>

              {/* Vehicles Category */}
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, assetType: 'Vehicle', useManualValue: true, isLiability: false, ticker: '', units: '1' }))}
                style={{
                  padding: '16px 12px',
                  border: isVehicleEquipment && !isLiability ? '2px solid #667eea' : '2px solid var(--color-border)',
                  borderRadius: '8px',
                  background: isVehicleEquipment && !isLiability ? 'rgba(102, 126, 234, 0.1)' : 'var(--color-surface)',
                  cursor: 'pointer',
                  textAlign: 'center',
                  transition: 'all 0.2s',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: 'var(--color-text-primary)'
                }}
                aria-pressed={isVehicleEquipment && !isLiability}
              >
                <div style={{ fontSize: '24px', marginBottom: '4px' }}>🚗</div>
                <div>Vehicles</div>
                <div style={{ fontSize: '11px', opacity: 0.7, marginTop: '4px' }}>Cars, equipment</div>
              </button>

              {/* Liabilities Category */}
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, assetType: 'Mortgage', useManualValue: true, isLiability: true, ticker: '', units: '1' }))}
                style={{
                  padding: '16px 12px',
                  border: isLiability ? '2px solid #EF4444' : '2px solid var(--color-border)',
                  borderRadius: '8px',
                  background: isLiability ? 'rgba(239, 68, 68, 0.1)' : 'var(--color-surface)',
                  cursor: 'pointer',
                  textAlign: 'center',
                  transition: 'all 0.2s',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: 'var(--color-text-primary)'
                }}
                aria-pressed={isLiability}
              >
                <div style={{ fontSize: '24px', marginBottom: '4px' }}>💳</div>
                <div>Liabilities</div>
                <div style={{ fontSize: '11px', opacity: 0.7, marginTop: '4px' }}>Debts, loans</div>
              </button>
            </div>

            {/* Sub-type selection for Investments */}
            {!isManualAsset && !isLiability && (
              <div style={{ marginTop: '12px' }}>
                <label style={{ fontSize: '14px', fontWeight: '500', marginBottom: '8px', display: 'block' }}>
                  Investment Type *
                </label>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {['Stock', 'ETF', 'Cryptocurrency', 'Precious Metal'].map(type => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, assetType: type }))}
                      style={{
                        padding: '8px 16px',
                        border: formData.assetType === type ? '2px solid #667eea' : '1px solid var(--color-border)',
                        borderRadius: '6px',
                        background: formData.assetType === type ? 'rgba(102, 126, 234, 0.15)' : 'var(--color-surface)',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: formData.assetType === type ? '600' : '400',
                        color: 'var(--color-text-primary)',
                        transition: 'all 0.2s'
                      }}
                      aria-pressed={formData.assetType === type}
                    >
                      {type}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Sub-type selection for Vehicles/Equipment */}
            {isVehicleEquipment && !isLiability && (
              <div style={{ marginTop: '12px' }}>
                <label style={{ fontSize: '14px', fontWeight: '500', marginBottom: '8px', display: 'block' }}>
                  Specific Type *
                </label>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {[
                    { value: 'Vehicle', label: '🚗 Vehicle' },
                    { value: 'Equipment', label: '🔧 Equipment' },
                    { value: 'Other High-Value Asset', label: '💎 Other' }
                  ].map(type => (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, assetType: type.value }))}
                      style={{
                        padding: '8px 16px',
                        border: formData.assetType === type.value ? '2px solid #667eea' : '1px solid var(--color-border)',
                        borderRadius: '6px',
                        background: formData.assetType === type.value ? 'rgba(102, 126, 234, 0.15)' : 'var(--color-surface)',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: formData.assetType === type.value ? '600' : '400',
                        color: 'var(--color-text-primary)',
                        transition: 'all 0.2s'
                      }}
                      aria-pressed={formData.assetType === type.value}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Sub-type selection for Liabilities */}
            {isLiability && (
              <div style={{ marginTop: '12px' }}>
                <label style={{ fontSize: '14px', fontWeight: '500', marginBottom: '8px', display: 'block' }}>
                  Liability Type *
                </label>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {[
                    { value: 'Mortgage', label: '🏠 Mortgage' },
                    { value: 'Loan', label: '💳 Loan' },
                    { value: 'Credit Card Debt', label: '💳 Credit Card' },
                    { value: 'Other Liability', label: '💳 Other' }
                  ].map(type => (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, assetType: type.value }))}
                      style={{
                        padding: '8px 16px',
                        border: formData.assetType === type.value ? '2px solid #EF4444' : '1px solid var(--color-border)',
                        borderRadius: '6px',
                        background: formData.assetType === type.value ? 'rgba(239, 68, 68, 0.15)' : 'var(--color-surface)',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: formData.assetType === type.value ? '600' : '400',
                        color: 'var(--color-text-primary)',
                        transition: 'all 0.2s'
                      }}
                      aria-pressed={formData.assetType === type.value}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Info banner for selected type */}
          {isManualAsset && (
            <div style={{
              padding: '12px 16px',
              marginBottom: '20px',
              backgroundColor: isLiability ? 'rgba(239, 68, 68, 0.1)' : isVehicleEquipment ? 'rgba(139, 92, 246, 0.1)' : 'rgba(79, 70, 229, 0.1)',
              border: `1px solid ${isLiability ? '#EF4444' : isVehicleEquipment ? '#8B5CF6' : '#4F46E5'}`,
              borderRadius: '6px',
              fontSize: '0.9em',
              color: '#333'
            }}>
              <strong>{isLiability ? '💳 Liability' : isVehicleEquipment ? '🚗 Vehicle/Equipment Asset' : '✏️ Manual Asset'}</strong>
              <p style={{ margin: '6px 0 0 0' }}>
                {isLiability ?
                  'This liability will reduce your net worth. Enter the original debt amount and current balance.' :
                  isVehicleEquipment ?
                  'Track high-value assets with depreciation. Enter purchase and current value.' :
                  'This asset requires manual value entry. You\'ll need to update the current value periodically.'
                }
              </p>
            </div>
          )}

          {exchangeRateError && !isManualAsset && (
            <div className="warning-message" role="alert" style={{
              padding: '12px 16px',
              marginBottom: '16px',
              backgroundColor: '#fff3cd',
              border: '1px solid #ffc107',
              borderRadius: '6px',
              color: '#856404',
              fontSize: '0.9em'
            }}>
              <strong>⚠️ Exchange Rate Service:</strong> {exchangeRateError}
            </div>
          )}

          {/* STEP 2: Tailored Form Fields */}

          {/* Name and identifier fields */}
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="assetName">
                {isVehicleEquipment ? 'Vehicle/Equipment Name *' :
                 isRealEstate ? 'Property Name *' :
                 formData.assetType === 'Cash/Bank Account' ? 'Account Name *' :
                 isLiability ? 'Liability Description *' :
                 'Asset Name *'}
              </label>
              <input
                ref={firstInputRef}
                type="text"
                id="assetName"
                name="assetName"
                value={formData.assetName}
                onChange={handleChange}
                required
                placeholder={
                  isVehicleEquipment ? 'e.g., 2020 Toyota Camry' :
                  isRealEstate ? 'e.g., 123 Main St' :
                  formData.assetType === 'Cash/Bank Account' ? 'e.g., Chase Checking' :
                  isLiability ? 'e.g., Home Mortgage' :
                  'e.g., Apple Inc.'
                }
              />
            </div>

            {!isManualAsset && (
              <div className="form-group">
                <label htmlFor="ticker">
                  {formData.assetType === 'Cryptocurrency' ? 'Symbol *' : 'Ticker Symbol *'}
                </label>
                <input
                  type="text"
                  id="ticker"
                  name="ticker"
                  value={formData.ticker}
                  onChange={handleChange}
                  required
                  placeholder={formData.assetType === 'Cryptocurrency' ? 'e.g., BTC' : 'e.g., AAPL'}
                />
              </div>
            )}
          </div>

          {/* Currency selection */}
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="currency">Currency *</label>
              <select
                id="currency"
                name="currency"
                value={formData.currency}
                onChange={handleChange}
                required
              >
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
                <option value="GBP">GBP</option>
                <option value="GBp">GBp</option>
                <option value="SEK">SEK</option>
                <option value="NOK">NOK</option>
                <option value="HKD">HKD</option>
              </select>
            </div>

            {/* Date field for all types */}
            <div className="form-group">
              <label htmlFor="dateAcquired">
                {isLiability ? 'Date Originated' :
                 isVehicleEquipment ? 'Purchase Date' :
                 isRealEstate ? 'Acquisition Date' :
                 'Date Acquired'}
              </label>
              <input
                type="date"
                id="dateAcquired"
                name="dateAcquired"
                value={formData.dateAcquired}
                onChange={handleChange}
              />
            </div>
          </div>

          {/* Real Estate Sub-Type (conditional) */}
          {isRealEstate && (
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="subType">Property Type</label>
                <select
                  id="subType"
                  name="subType"
                  value={formData.subType}
                  onChange={handleChange}
                >
                  <option value="">-- Select Property Type (Optional) --</option>
                  {REAL_ESTATE_SUBTYPES.map(subType => (
                    <option key={subType} value={subType}>{subType}</option>
                  ))}
                </select>
                <small className="form-help">Optional: Helps categorize your real estate holdings</small>
              </div>
            </div>
          )}

          {/* Conditional fields based on asset type */}
          {!isManualAsset && (
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="units">Units *</label>
                <input
                  type="number"
                  id="units"
                  name="units"
                  value={formData.units}
                  onChange={handleChange}
                  required
                  step="0.0001"
                  placeholder="e.g., 10.5"
                />
              </div>

              <div className="form-group">
                <label htmlFor="purchasePrice">Purchase Price *</label>
                <input
                  type="number"
                  id="purchasePrice"
                  name="purchasePrice"
                  value={formData.purchasePrice}
                  onChange={handleChange}
                  required
                  step="0.01"
                  placeholder="e.g., 150.00"
                />
              </div>
            </div>
          )}

          {/* Manual asset fields */}
          {isManualAsset && (
            <>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="purchasePrice">
                    {isLiability ? 'Original Amount (Principal) *' : 'Purchase Price *'}
                  </label>
                  <input
                    type="number"
                    id="purchasePrice"
                    name="purchasePrice"
                    value={formData.purchasePrice}
                    onChange={handleChange}
                    required
                    step="0.01"
                    placeholder={isLiability ? 'e.g., 400000' : 'e.g., 500000'}
                  />
                  <small style={{ color: '#666', fontSize: '0.85em' }}>
                    {isLiability ? 'Original debt amount when loan was taken' : 'Original purchase/acquisition price'}
                  </small>
                </div>

                <div className="form-group">
                  <label htmlFor="manualCurrentValue">
                    {isLiability ? 'Current Balance *' : 'Current Value *'}
                  </label>
                  <input
                    type="number"
                    id="manualCurrentValue"
                    name="manualCurrentValue"
                    value={formData.manualCurrentValue}
                    onChange={handleChange}
                    required
                    step="0.01"
                    placeholder={isLiability ? 'e.g., 250000' : 'e.g., 650000'}
                  />
                  <small style={{ color: '#666', fontSize: '0.85em' }}>
                    {isLiability ? 'Current outstanding balance' : 'Current market value or account balance'}
                  </small>
                </div>
              </div>

              {/* Liability-specific fields */}
              {isLiability && (
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="interestRate">Interest Rate (%)</label>
                    <input
                      type="number"
                      id="interestRate"
                      name="interestRate"
                      value={formData.interestRate}
                      onChange={handleChange}
                      step="0.01"
                      placeholder="e.g., 3.5"
                    />
                    <small style={{ color: '#666', fontSize: '0.85em' }}>Annual interest rate</small>
                  </div>

                  <div className="form-group">
                    <label htmlFor="monthlyPayment">Monthly Payment</label>
                    <input
                      type="number"
                      id="monthlyPayment"
                      name="monthlyPayment"
                      value={formData.monthlyPayment}
                      onChange={handleChange}
                      step="0.01"
                      placeholder="e.g., 1800"
                    />
                  </div>
                </div>
              )}

              {isLiability && formData.assetType === 'Mortgage' && (
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="maturityDate">Maturity Date</label>
                    <input
                      type="date"
                      id="maturityDate"
                      name="maturityDate"
                      value={formData.maturityDate}
                      onChange={handleChange}
                    />
                    <small style={{ color: '#666', fontSize: '0.85em' }}>When loan will be fully paid</small>
                  </div>

                  <div className="form-group">
                    <label htmlFor="linkedAssetId">Linked Property (Optional)</label>
                    <select
                      id="linkedAssetId"
                      name="linkedAssetId"
                      value={formData.linkedAssetId}
                      onChange={handleChange}
                    >
                      <option value="">-- No linked property --</option>
                      {realEstateHoldings.map(property => (
                        <option key={property.holdingId} value={property.holdingId}>
                          {property.assetName} - {property.manualCurrentValue ? `€${property.manualCurrentValue.toLocaleString()}` : 'N/A'}
                        </option>
                      ))}
                    </select>
                    <small style={{ color: '#666', fontSize: '0.85em' }}>
                      Link this mortgage to a property to track equity (property value - mortgage balance)
                    </small>
                  </div>
                </div>
              )}

              {/* Vehicle/Equipment-specific fields */}
              {isVehicleEquipment && (
                <>
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="vinOrSerial">VIN / Serial Number</label>
                      <input
                        type="text"
                        id="vinOrSerial"
                        name="vinOrSerial"
                        value={formData.vinOrSerial}
                        onChange={handleChange}
                        placeholder="e.g., 1HGBH41JXMN109186"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="condition">Condition</label>
                      <select
                        id="condition"
                        name="condition"
                        value={formData.condition}
                        onChange={handleChange}
                      >
                        <option value="">-- Select Condition --</option>
                        <option value="Excellent">Excellent</option>
                        <option value="Good">Good</option>
                        <option value="Fair">Fair</option>
                        <option value="Poor">Poor</option>
                      </select>
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="depreciationRate">Annual Depreciation Rate (%)</label>
                      <input
                        type="number"
                        id="depreciationRate"
                        name="depreciationRate"
                        value={formData.depreciationRate}
                        onChange={handleChange}
                        step="0.1"
                        placeholder="e.g., 15"
                      />
                      <small style={{ color: '#666', fontSize: '0.85em' }}>
                        Typical: Cars 15-20%, Equipment 10-15%
                      </small>
                    </div>

                    <div className="form-group">
                      <label htmlFor="insuranceDetails">Insurance Details</label>
                      <input
                        type="text"
                        id="insuranceDetails"
                        name="insuranceDetails"
                        value={formData.insuranceDetails}
                        onChange={handleChange}
                        placeholder="e.g., Policy #12345, Geico"
                      />
                    </div>
                  </div>
                </>
              )}

              <div className="form-row">
                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <label htmlFor="notes">
                    {isVehicleEquipment ? 'Notes / Maintenance Log' : 'Notes / Description'}
                  </label>
                  <textarea
                    id="notes"
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                    rows="3"
                    placeholder={isLiability ?
                      'e.g., 30-year fixed mortgage, Bank of America account #12345' :
                      isVehicleEquipment ?
                      'e.g., 2020 Toyota Camry, serviced at 50k miles, new tires 2023' :
                      'e.g., 123 Main St, San Francisco or Chase Checking account'
                    }
                    style={{ width: '100%', padding: '8px', fontFamily: 'inherit' }}
                  />
                </div>
              </div>
            </>
          )}

          {/* Platform field - only for investments */}
          {!isManualAsset && (
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="platform">
                  {formData.assetType === 'Cryptocurrency' ? 'Exchange/Wallet' : 'Platform/Broker'}
                </label>
                <input
                  type="text"
                  id="platform"
                  name="platform"
                  value={formData.platform}
                  onChange={handleChange}
                  placeholder={
                    formData.assetType === 'Cryptocurrency' ? 'e.g., Coinbase, Binance' : 'e.g., Robinhood, eToro, Interactive Brokers'
                  }
                />
              </div>

              <div className="form-group">
                <label htmlFor="purchaseExchangeRate">
                  Exchange Rate (1 {formData.currency} = ? EUR)
                  {fetchingRate && <span style={{ marginLeft: '8px', fontSize: '0.9em', color: '#999' }}>Fetching...</span>}
                </label>
                <input
                  type="number"
                  id="purchaseExchangeRate"
                  name="purchaseExchangeRate"
                  value={formData.purchaseExchangeRate}
                  onChange={handleChange}
                  step="0.0001"
                  placeholder="e.g., 0.9600"
                  disabled={fetchingRate}
                  title={`Exchange rate at purchase time (how many EUR for 1 ${formData.currency})`}
                />
                <small style={{ color: '#666', fontSize: '0.85em' }}>
                  Auto-filled with historical rate. Editable if needed.
                </small>
              </div>
            </div>
          )}

          <div className="form-actions">
            <button type="button" onClick={onCancel} className="btn-cancel" disabled={loading}>
              Cancel
            </button>
            <button type="submit" className="btn-submit" disabled={loading}>
              {loading ? 'Saving...' : (isEdit ? 'Update' : 'Add')} Holding
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default HoldingForm;